/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, useState, type FormEvent, type ReactElement, Fragment } from 'react';
import Textfield from '@atlaskit/textfield/text-field';
import Box from '@atlaskit/primitives/box';
import Stack from '@atlaskit/primitives/stack';
import { xcss } from '@atlaskit/primitives/xcss';
import SearchIcon from '@atlaskit/icon/core/search';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import Heading from '@atlaskit/heading/heading';
import HeadingContextProvider from '@atlaskit/heading/heading-context';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
	FlyoutBody,
	FlyoutHeader,
} from '@atlaskit/side-nav-items/flyout-menu-item';
import { MenuSection } from '@atlaskit/side-nav-items/menu-section';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';

import type { AlertsMenuItemContent$data } from '../__generated__/AlertsMenuItemContent.graphql.ts';

const alertsFlyoutTitleMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.alerts',

	defaultMessage: 'Alerts',
	description: 'Assets dropdown item to link customer to Operations Alerts page.',
});

const alertsFlyoutCloseButtonMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.close-button',

	defaultMessage: 'Close alerts menu',
	description: 'Message for the close button in the alerts menu.',
});

const alertsFlyoutSearchPlaceholderMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.view-all-filters-search-placeholder',

	defaultMessage: 'Search',
	description: 'Placeholder text for the search input in the alert list view all filters popup.',
});

const alertsFlyoutViewAllButtonMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.view-all-filters-popup-menu-button-text',

	defaultMessage: 'View all',
	description: 'Text for the button that opens the alert list view all filters popup.',
});

const alertsFlyoutDefaultFiltersMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.default-filters',

	defaultMessage: 'Default filters',
	description:
		'Expandable menu item label displayed in the operations sidebar. When clicked, shows pre-configured filter options for alerts.',
});

const alertsFlyoutSharedWithMeMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.shared-with-me',

	defaultMessage: 'Shared with me',
	description:
		'Expandable menu item label displayed in the operations sidebar. Shows alerts and searches that other users have shared with the current user.',
});

const alertsFlyoutCreatedByMeMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.created-by-me',

	defaultMessage: 'Created by me',
	description:
		'Expandable menu item label displayed in the operations sidebar. Shows alerts and searches that the current user has created.',
});

const alertsFlyoutNoResultsFoundMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.view-all-filters-no-results-found-text',

	defaultMessage: 'No search results found',
	description: 'Text displayed when no results are found in the alert list view all filters popup.',
});

type filterType = Array<
	NonNullable<
		NonNullable<NonNullable<AlertsMenuItemContent$data>['opsgenie']>['savedSearches']
	>['createdByMe'][number] & {
		component: ReactElement;
	}
>;

export const AlertsFlyoutMenuItem = ({
	defaultFilterWithComponentList,
	sharedWithMeWithComponentList,
	createdByMeWithComponentList,
	allFiltersWithComponentList,
}: {
	defaultFilterWithComponentList: filterType;
	sharedWithMeWithComponentList: filterType;
	createdByMeWithComponentList: filterType;
	allFiltersWithComponentList: filterType;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [textFilterValue, setTextFilterValue] = useState('');
	const [foundItems, setFoundItems] = useState<ReactElement[]>([]);

	const handleChange = useCallback(
		(event: FormEvent<HTMLInputElement>) => {
			const value = event.currentTarget.value;
			setTextFilterValue(value);

			const foundItemsList = allFiltersWithComponentList.filter((item) =>
				item.name.toLowerCase().includes(value.toLowerCase()),
			);

			const components = foundItemsList.map((item) => item.component);

			setFoundItems(components);
		},
		[allFiltersWithComponentList],
	);

	const onStopSavedSearch = useCallback(() => {
		const savedSearchesEvent = createAnalyticsEvent({
			action: 'blurred',
			actionSubject: 'jsmViewAllSavedSearchesFlyoutSearch',
		});
		fireUIAnalytics(savedSearchesEvent, 'stopViewAllQueueSearch');
	}, [createAnalyticsEvent]);

	// New header component for the flyout menu behind fg('platform_dst_nav4_flyout_menu_slots_close_button')
	const header = (
		<FlyoutHeader
			title={formatMessage(alertsFlyoutTitleMessage)}
			closeButtonLabel={formatMessage(alertsFlyoutCloseButtonMessage)}
		>
			<Textfield
				isCompact
				value={textFilterValue}
				onChange={handleChange}
				aria-label={formatMessage(alertsFlyoutSearchPlaceholderMessage)}
				placeholder={formatMessage(alertsFlyoutSearchPlaceholderMessage)}
				elemBeforeInput={<SearchIcon label="" spacing="spacious" color="currentColor" />}
				onBlur={onStopSavedSearch}
			/>
		</FlyoutHeader>
	);

	const MenuSectionBehindFG = fg('platform_dst_nav4_flyout_menu_slots_close_button')
		? Fragment
		: MenuSection;

	return (
		<FlyoutMenuItem>
			<FlyoutMenuItemTrigger
				elemBefore={<AlignTextLeftIcon label="" spacing="spacious" color="currentColor" />}
			>
				{formatMessage(alertsFlyoutViewAllButtonMessage)}
			</FlyoutMenuItemTrigger>
			<FlyoutMenuItemContent
				maxHeight={fg('platform_dst_nav4_flyout_menu_slots_close_button') ? 460 : undefined}
			>
				<HeadingContextProvider value={2}>
					<MenuSectionBehindFG>
						{fg('platform_dst_nav4_flyout_menu_slots_close_button') ? (
							header
						) : (
							<Box xcss={headerStyles}>
								<Box paddingBlock="space.100">
									<Heading size="xsmall">{formatMessage(alertsFlyoutTitleMessage)}</Heading>
								</Box>
								<Box xcss={textfieldStyles}>
									<Textfield
										isCompact
										value={textFilterValue}
										onChange={handleChange}
										aria-label={formatMessage(alertsFlyoutSearchPlaceholderMessage)}
										placeholder={formatMessage(alertsFlyoutSearchPlaceholderMessage)}
										elemBeforeInput={
											<SearchIcon label="" spacing="spacious" color="currentColor" />
										}
										onBlur={onStopSavedSearch}
									/>
								</Box>
							</Box>
						)}
						<StackWrapper>
							{!textFilterValue.length ? (
								<>
									<HeadingContextProvider>
										<Box paddingBlock="space.100">
											<Heading size="xxsmall">
												{formatMessage(alertsFlyoutDefaultFiltersMessage)}
											</Heading>
										</Box>
										{fg('jsm-corecaps-ally-fixes-batch-6') ? (
											<MenuList>
												{defaultFilterWithComponentList.map((item) => item.component)}
											</MenuList>
										) : (
											<>{defaultFilterWithComponentList.map((item) => item.component)}</>
										)}
									</HeadingContextProvider>
									{sharedWithMeWithComponentList && sharedWithMeWithComponentList.length > 0 && (
										<HeadingContextProvider>
											<Box paddingBlock="space.100">
												<Heading size="xxsmall">
													{formatMessage(alertsFlyoutSharedWithMeMessage)}
												</Heading>
											</Box>
											{fg('jsm-corecaps-ally-fixes-batch-6') ? (
												<MenuList>
													{sharedWithMeWithComponentList.map((item) => item.component)}
												</MenuList>
											) : (
												<>{sharedWithMeWithComponentList.map((item) => item.component)}</>
											)}
										</HeadingContextProvider>
									)}
									{createdByMeWithComponentList && createdByMeWithComponentList.length > 0 && (
										<HeadingContextProvider>
											<Box paddingBlock="space.100">
												<Heading size="xxsmall">
													{formatMessage(alertsFlyoutCreatedByMeMessage)}
												</Heading>
											</Box>
											{fg('jsm-corecaps-ally-fixes-batch-6') ? (
												<MenuList>
													{createdByMeWithComponentList.map((item) => item.component)}
												</MenuList>
											) : (
												<>{createdByMeWithComponentList.map((item) => item.component)}</>
											)}
										</HeadingContextProvider>
									)}
								</>
							) : (
								<>
									{foundItems.length === 0 ? (
										<Box
											key="no-item-found"
											paddingBlock="space.100"
											xcss={noSearchResultsFoundStyles}
										>
											{formatMessage(alertsFlyoutNoResultsFoundMessage)}
										</Box>
									) : (
										<SearchResults foundItems={foundItems} />
									)}
								</>
							)}
						</StackWrapper>
					</MenuSectionBehindFG>
				</HeadingContextProvider>
			</FlyoutMenuItemContent>
		</FlyoutMenuItem>
	);
};

const SearchResults = ({ foundItems }: { foundItems: ReactElement[] }) =>
	fg('jsm-corecaps-ally-fixes-batch-6') ? <MenuList>{foundItems}</MenuList> : <>{foundItems}</>;

const StackWrapper = ({ children }: { children: React.ReactNode }) =>
	fg('platform_dst_nav4_flyout_menu_slots_close_button') ? (
		<FlyoutBody>{children}</FlyoutBody>
	) : (
		<Stack space="space.050" xcss={flyoutContentStyles}>
			{children}
		</Stack>
	);

const headerStyles = xcss({
	marginBottom: 'space.150',
	backgroundColor: 'utility.elevation.surface.current',
});

const textfieldStyles = xcss({
	paddingRight: 'space.100',
});

const noSearchResultsFoundStyles = xcss({
	fontStyle: 'italic',
});

const flyoutContentStyles = xcss({
	maxHeight: '500px',
	overflowY: 'auto',
});
